import {
  EnumPersonaGiuridicaDTOStatoAccreditamento,
  EnumPersonaGiuridicaDTOTipoPg,
} from '@framework/api-client';
import type {
  PersonaGiuridicaDTO,
  PersonaGiuridicaDTOStatoAccreditamento,
  PersonaGiuridicaDTOTipoPg,
} from '@framework/api-client';
import { getCountryName } from '@framework/common-web';
import { t } from '@framework/i18n';

export interface LegalEntityTypeLabelKeys {
  privateType: string;
  publicType: string;
}

export function getLegalEntityInitials(name: string | null | undefined) {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');
}

export function getLegalEntityTypeLabel(
  type: PersonaGiuridicaDTOTipoPg | undefined,
  keys: LegalEntityTypeLabelKeys = {
    privateType: 'legal-entity.type-private',
    publicType: 'legal-entity.type-public',
  },
): string | null {
  if (!type) return null;
  switch (type) {
    case EnumPersonaGiuridicaDTOTipoPg.PUBBLICA:
      return t(keys.publicType, 'Pubblica');
    case EnumPersonaGiuridicaDTOTipoPg.PRIVATA:
      return t(keys.privateType, 'Privata');
    default:
      return null;
  }
}

export function getLegalEntityStatusLabel(
  state: PersonaGiuridicaDTOStatoAccreditamento | undefined,
): string | null {
  if (!state) return null;
  switch (state) {
    case EnumPersonaGiuridicaDTOStatoAccreditamento.BOZZA:
      return t('legal-entity.status-draft', 'Bozza');
    case EnumPersonaGiuridicaDTOStatoAccreditamento.VALIDATA:
      return t('legal-entity.status-validated', 'Validata');
    case EnumPersonaGiuridicaDTOStatoAccreditamento.REVOCATA:
      return t('legal-entity.status-revoked', 'Revocata');
    default:
      return null;
  }
}

/**
 * Compone l'indirizzo della sede legale in un'unica stringa, saltando i campi
 * vuoti. La provincia è inclusa solo se è presente una località (cap/comune).
 *
 * `paeseSedeLegale` è un codice ANPR (es. '401' o '100'), non un nome: viene
 * quindi decodificato nel nome del paese (es. 'ALGERIA', 'ITALIA'). Per un
 * codice non presente in anagrafica si ripiega sul valore grezzo.
 *
 * Ritorna null se non c'è alcun dato.
 */
export function formatLegalEntityAddress(
  entity: Pick<
    PersonaGiuridicaDTO,
    'indirizzo' | 'cap' | 'comune' | 'provincia' | 'paeseSedeLegale'
  >,
): string | null {
  const locality = [entity.cap, entity.comune]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
  const localityWithProvince =
    locality && entity.provincia?.trim()
      ? `${locality} (${entity.provincia.trim()})`
      : locality;

  const countryCode = entity.paeseSedeLegale?.trim();
  const country = getCountryName(countryCode) ?? countryCode;

  const parts = [
    entity.indirizzo?.trim(),
    localityWithProvince,
    country,
  ].filter((part): part is string => !!part);

  return parts.length ? parts.join(', ') : null;
}
